import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { useAuthStore } from '../stores/authStore'
import { AuthProgressIndicator } from '../components/auth/AuthProgressIndicator'
import { UserInfoForm } from '../components/auth/UserInfoForm'
import { OTPVerification } from '../components/auth/OTPVerification'
import { TermsAcceptance } from '../components/auth/TermsAcceptance'
import { StratIQXHeader } from '../components/ui/StratIQXHeader'
import { ArrowLeft } from 'lucide-react'

export const AuthPage: React.FC = () => {
  const navigate = useNavigate()
  const { currentStep, resetAuth, isAuthenticated } = useAuthStore()

  // Redirect authenticated users to onboarding
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/onboarding')
    }
  }, [isAuthenticated, navigate])

  const handleBackToInfo = () => {
    resetAuth()
    navigate('/onboarding-info')
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'userInfo':
        return <UserInfoForm />
      case 'otpVerification':
        return <OTPVerification />
      case 'consentAcceptance':
        return <TermsAcceptance />
      default:
        return <UserInfoForm />
    }
  }

  // Don't render if already authenticated (will redirect)
  if (isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <Button 
              variant="ghost" 
              onClick={handleBackToInfo}
              className="text-blue-600 hover:text-blue-800"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Setup Info
            </Button>
          </div>
          
          <StratIQXHeader
            subtitle="Secure Access | StratIQX"
            title="Secure Access Required"
            description="Verify your identity to access StratIQX strategic intelligence platform"
          />
        </div>

        {/* Progress Indicator */}
        <AuthProgressIndicator currentStep={currentStep} />

        {/* Current Step Content */}
        <div className="flex justify-center">
          {renderCurrentStep()}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Secure authentication powered by StratIQX • All data encrypted
          </p>
        </div>
      </div>
    </div>
  )
}
